import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import DropDownMultiple from "@components/ui/dropdown/DropDownMultiple";
import Button from "@components/ui/buttons/Button";
import { useEffect, useRef, useState } from "react";
import TextField from "@components/ui/textfields/TextField";
import { PartnerInfos } from "types";
import { showNotification } from "context/notification.service";
import { useProjectStore } from "@store/projectStore";
import { useDataStore } from "@store/user_org";
import { COMPANY_TYPE_OPTIONS, COMPANY_TYPE_LABELS, HIDDEN_COMPANY_TYPES } from "@constants/partnerTypes";
import { partnersApi } from "@services/api";

interface PendingDecennale {
  key: string;
  startDate: string;
  endDate: string;
  file: File | null;
}

type CheckStatus = "idle" | "checking" | "available" | "similar" | "exact";

interface MatchInfo {
  id: string;
  company_name: string;
  display_name: string;
  email: string;
  phone_number: string;
  company_types: string[];
}

interface CheckResult {
  status: CheckStatus;
  matches: MatchInfo[];
}

function useDuplicateCheck(
  orgId: string,
  companyName: string,
  email: string,
  companyEmail: string,
  delay = 600,
): CheckResult {
  const [result, setResult] = useState<CheckResult>({
    status: "idle",
    matches: [],
  });
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const abortRef = useRef<AbortController>(undefined);

  useEffect(() => {
    const cn = companyName.trim();
    const em = email.trim();
    const ce = companyEmail.trim();

    // Need at least one field with 3+ chars
    if (cn.length < 3 && em.length < 3 && ce.length < 3) {
      setResult({ status: "idle", matches: [] });
      return;
    }

    setResult((prev) => ({ ...prev, status: "checking" }));

    if (timerRef.current) clearTimeout(timerRef.current);
    if (abortRef.current) abortRef.current.abort();

    timerRef.current = setTimeout(async () => {
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const res = await partnersApi.checkDuplicate({
          org_id: orgId,
          company_name: cn || undefined,
          email: em || undefined,
          company_email: ce || undefined,
        });

        if (!controller.signal.aborted) {
          setResult({
            status: res.status,
            matches: res.matches,
          });
        }
      } catch {
        if (!controller.signal.aborted) {
          setResult({ status: "idle", matches: [] });
        }
      }
    }, delay);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [orgId, companyName, email, companyEmail, delay]);

  return result;
}

function StatusBanner({ result }: { result: CheckResult }) {
  if (result.status === "idle") return null;

  if (result.status === "checking") {
    return (
      <div className="mt-2 rounded-lg overflow-hidden">
        <div className="h-1.5 bg-blue-100 rounded-lg overflow-hidden">
          <div
            className="h-full bg-blue-500 rounded-lg"
            style={{
              animation: "dup-check-bar 1.2s ease-in-out infinite",
              width: "40%",
            }}
          />
        </div>
        <style>{`
          @keyframes dup-check-bar {
            0% { margin-left: 0; }
            50% { margin-left: 60%; }
            100% { margin-left: 0; }
          }
        `}</style>
      </div>
    );
  }

  if (result.status === "available") {
    return (
      <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-green-50 rounded-lg border border-green-200">
        <div className="w-2 h-2 rounded-full bg-green-500" />
        <span className="text-h3 text-green-700">Disponible</span>
      </div>
    );
  }

  const isExact = result.status === "exact";
  const bgColor = isExact ? "bg-red-50" : "bg-orange-50";
  const borderColor = isExact ? "border-red-200" : "border-orange-200";
  const dotColor = isExact ? "bg-red-500" : "bg-orange-500";
  const textColor = isExact ? "text-red-700" : "text-orange-700";
  const label = isExact
    ? "Artisan identique existant"
    : "Artisan(s) similaire(s)";

  return (
    <div
      className={`mt-2 flex flex-col gap-2 px-3 py-2 ${bgColor} rounded-lg border ${borderColor}`}
    >
      <div className="flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full ${dotColor}`} />
        <span className={`text-h3 font-medium ${textColor}`}>{label}</span>
      </div>
      {result.matches.map((m) => (
        <div
          key={m.id}
          className="flex flex-row items-center gap-2 bg-white rounded-lg px-3 py-2 border border-neutral-100"
        >
          <div className="flex flex-col flex-1 min-w-0">
            <span className="text-h3 font-medium text-neutral-800 truncate">
              {m.company_name || m.display_name}
            </span>
            <span className="text-h4 text-neutral-400 truncate">
              {m.display_name}
              {m.email ? ` — ${m.email}` : ""}
              {m.phone_number ? ` — ${m.phone_number}` : ""}
            </span>
          </div>
          <span className="text-h4 text-neutral-400 shrink-0">
            {(m.company_types || [])
              .filter((t) => !HIDDEN_COMPANY_TYPES.has(t))
              .map((t) => COMPANY_TYPE_LABELS[t] || t)
              .join(", ")}
          </span>
        </div>
      ))}
    </div>
  );
}

export const CreatePartnerModal = ({
  onClose,
  onSave,
}: {
  onClose: () => void;
  onSave?: (partner: PartnerInfos) => void;
}) => {
  const addPartner = useProjectStore((state) => state.addPartner);
  const userData = useDataStore((state) => state.userData);
  const [partner, setPartner] = useState<PartnerInfos>({
    id: `partner_${Date.now()}`,
    display_name: "",
    company_types: ["autre"],
  });

  const duplicateCheck = useDuplicateCheck(
    userData?.org_id || "",
    partner.company_name || "",
    partner.email || "",
    partner.company_email || "",
  );

  const [saving, setSaving] = useState(false);
  const [decennales, setDecennales] = useState<PendingDecennale[]>([]);
  const updatePartnerInStore = useProjectStore((state) => state.updatePartner);

  const addDecennaleRow = () =>
    setDecennales((prev) => [
      ...prev,
      { key: `dec_${Date.now()}_${prev.length}`, startDate: "", endDate: "", file: null },
    ]);
  const updateDecennaleRow = (key: string, patch: Partial<PendingDecennale>) =>
    setDecennales((prev) => prev.map((d) => (d.key === key ? { ...d, ...patch } : d)));
  const removeDecennaleRow = (key: string) =>
    setDecennales((prev) => prev.filter((d) => d.key !== key));

  async function handleSave() {
    if (!partner.display_name && !partner.company_name) {
      showNotification({
        type: "error",
        message: "Veuillez renseigner un nom ou une entreprise",
      });
      return;
    }
    // Validate pending decennales before creating the partner so we don't
    // end up with a half-finished record on the server.
    for (const d of decennales) {
      if (!d.startDate || !d.endDate || !d.file) {
        showNotification({
          type: "error",
          message: "Chaque décennale doit avoir une date de début, une date de fin et un fichier.",
        });
        return;
      }
      if (d.startDate >= d.endDate) {
        showNotification({
          type: "error",
          message: "La date de fin d'une décennale doit être postérieure à la date de début.",
        });
        return;
      }
    }
    setSaving(true);
    try {
      const created = await addPartner(partner, userData?.org_id || "");
      let withDecennales = created;
      const failures: string[] = [];
      for (const d of decennales) {
        try {
          withDecennales = await partnersApi.uploadDecennale(
            created.id,
            d.startDate,
            d.endDate,
            d.file as File,
          );
        } catch (err: any) {
          failures.push(d.file?.name || `${d.startDate} → ${d.endDate}`);
          console.error("Decennale upload failed during partner creation:", err?.response?.data || err);
        }
      }
      if (decennales.length > 0) updatePartnerInStore(withDecennales);
      onSave?.(withDecennales);
      if (failures.length > 0) {
        showNotification({
          type: "error",
          message: `Artisan créé, mais échec d'upload pour : ${failures.join(", ")}`,
        });
      } else {
        showNotification({
          type: "success",
          message: "Artisan ajouté avec succès",
        });
      }
      onClose();
    } catch {
      showNotification({
        type: "error",
        message: "Erreur lors de la création de l'artisan",
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <BaseModal
      title="Nouvel intervenant"
      startIcon={<Icon1 name="intervenant" size={20} />}
      onClose={onClose}
    >
      <div className="bg-white flex flex-col gap-2 p-4">
        <div className="text-h3 text-neutral-400 font-semibold">Entreprise</div>
        <div className="flex flex-row gap-2">
          <div className="flex-1">
            <TextField
              label="Nom entreprise"
              placeholder="Construction ABC"
              value={partner.company_name || ""}
              onChange={(value) =>
                setPartner({ ...partner, company_name: value })
              }
            />
          </div>
          <div className="flex-1">
            <DropDownMultiple
              options={COMPANY_TYPE_OPTIONS}
              label="Type"
              value={partner.company_types}
              onSelect={(values) =>
                setPartner({
                  ...partner,
                  company_types: values as string[],
                })
              }
            />
          </div>
        </div>
        <div className="flex flex-row gap-2">
          <TextField
            label="Tel entreprise"
            placeholder="01 23 45 67 89"
            type="phone"
            value={partner.company_phone || ""}
            onChange={(value) =>
              setPartner({ ...partner, company_phone: value })
            }
          />
          <TextField
            label="Email entreprise"
            placeholder="contact@entreprise.fr"
            value={partner.company_email || ""}
            onChange={(value) =>
              setPartner({ ...partner, company_email: value })
            }
          />
        </div>
        <div className="flex flex-row gap-2">
          <TextField
            label="Adresse"
            placeholder="12 Rue de la Paix"
            value={partner.company_address || ""}
            onChange={(value) =>
              setPartner({ ...partner, company_address: value })
            }
          />
          <TextField
            label="Code postal"
            placeholder="75002"
            value={partner.company_postal || ""}
            onChange={(value) =>
              setPartner({ ...partner, company_postal: value })
            }
          />
        </div>
        <TextField
          label="Ville"
          placeholder="Paris"
          value={partner.company_city || ""}
          onChange={(value) =>
            setPartner({ ...partner, company_city: value })
          }
        />

        <div className="border-t border-neutral-200 my-2" />
        <div className="text-h3 text-neutral-400 font-semibold">Contact</div>
        <TextField
          label="Nom complet"
          placeholder="Sophie Martin"
          value={partner.display_name}
          onChange={(value) =>
            setPartner({ ...partner, display_name: value })
          }
        />
        <div className="flex flex-row gap-2">
          <TextField
            label="Téléphone"
            placeholder="06 12 34 56 78"
            type="phone"
            value={partner.phone_number || ""}
            onChange={(value) =>
              setPartner({ ...partner, phone_number: value })
            }
          />
          <TextField
            label="Email"
            placeholder="sophie@martin.fr"
            value={partner.email || ""}
            onChange={(value) => setPartner({ ...partner, email: value })}
          />
        </div>

        <StatusBanner result={duplicateCheck} />

        <div className="border-t border-neutral-200 my-2" />
        <div className="flex flex-row items-center justify-between">
          <div className="text-h3 text-neutral-400 font-semibold">
            Décennales (optionnel)
          </div>
        </div>
        {decennales.length === 0 && (
          <div className="text-h4 text-neutral-400">
            Aucune décennale ajoutée. Vous pourrez aussi en ajouter plus tard.
          </div>
        )}
        {decennales.map((d) => (
          <DecennaleRow
            key={d.key}
            row={d}
            onChange={(patch) => updateDecennaleRow(d.key, patch)}
            onRemove={() => removeDecennaleRow(d.key)}
          />
        ))}
        <SecondaryButton
          variant="small"
          startIcon={<Icon1 name="plus" size={14} />}
          onClick={addDecennaleRow}
        >
          Ajouter une décennale
        </SecondaryButton>
      </div>
      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>
          Annuler
        </SecondaryButton>
        <Button
          variant="small"
          label={saving ? "Enregistrement..." : "Enregistrer"}
          onClick={handleSave}
          disabled={saving}
        />
      </div>
    </BaseModal>
  );
};

function DecennaleRow({
  row,
  onChange,
  onRemove,
}: {
  row: PendingDecennale;
  onChange: (patch: Partial<PendingDecennale>) => void;
  onRemove: () => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  return (
    <div className="flex flex-col gap-2 px-3 py-3 border border-neutral-200 rounded-lg">
      <div className="flex flex-row gap-2 items-end">
        <div className="flex flex-col gap-1 flex-1">
          <label className="text-h4 text-neutral-500 font-medium">Date de début</label>
          <input
            type="date"
            value={row.startDate}
            onChange={(e) => onChange({ startDate: e.target.value })}
            className="border border-neutral-300 rounded-lg px-3 py-2 text-h3"
          />
        </div>
        <div className="flex flex-col gap-1 flex-1">
          <label className="text-h4 text-neutral-500 font-medium">Date de fin</label>
          <input
            type="date"
            value={row.endDate}
            onChange={(e) => onChange({ endDate: e.target.value })}
            className="border border-neutral-300 rounded-lg px-3 py-2 text-h3"
          />
        </div>
        <button
          type="button"
          onClick={onRemove}
          className="text-neutral-400 hover:text-red-500 p-2"
          aria-label="Supprimer cette décennale"
        >
          <Icon1 name="effacer2" size={16} />
        </button>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onChange({ file });
        }}
      />
      {row.file ? (
        <div
          className="flex items-center gap-2 px-3 py-2 border border-neutral-200 rounded-lg bg-neutral-50 cursor-pointer hover:bg-neutral-100"
          onClick={() => fileInputRef.current?.click()}
        >
          <Icon1 name="pdf" size={16} />
          <span className="text-h3 text-neutral-700 truncate flex-1">{row.file.name}</span>
          <button
            type="button"
            className="text-neutral-400 hover:text-red-500"
            onClick={(e) => {
              e.stopPropagation();
              onChange({ file: null });
            }}
          >
            <Icon1 name="effacer2" size={16} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="flex items-center gap-2 px-3 py-2 border border-dashed border-neutral-300 rounded-lg text-h3 text-neutral-400 hover:border-neutral-400 hover:text-neutral-600 transition-colors"
          onClick={() => fileInputRef.current?.click()}
        >
          <Icon1 name="plus" size={14} />
          Sélectionner un fichier PDF
        </button>
      )}
    </div>
  );
}
